import React from "react";
import { Box, Typography, Card, Chip, TextField, Button, Divider, ToggleButton, ToggleButtonGroup, Alert } from "@mui/material";
import { Wifi, Eye, EyeOff, AlertCircle } from "lucide-react";
import type { CwmpWlanParams, CwmpCapabilities } from "../hooks/useWifiVerification";

interface WifiConfigurationPanelProps {
  model: string;
  params: CwmpWlanParams;
  onParamsChange?: (updatedParams: CwmpWlanParams) => void;
  onChangesUpdate?: (changes: { [key: string]: any }) => void;
  onValidationErrorsChange?: (hasErrors: boolean) => void;
  capabilities?: CwmpCapabilities;
}

// Función para validar SSID (solo alfanuméricos sin acentos ni caracteres especiales)
const validateSSID = (value: string): string => {
  // Permite solo letras (A-Z, a-z), números (0-9), espacios, guiones y guiones bajos
  return value.replace(/[^a-zA-Z0-9\s\-_]/g, "").slice(0, 12);
};

// Función para validar contraseña
const validatePassword = (value: string): string => {
  return value.slice(0, 10);
};

interface WlanBandProps {
  title: string;
  band: "2.4GHz" | "5GHz";
  configNum: "1" | "5";
  config: { [key: string]: any };
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
  localSSID: string;
  setLocalSSID: (value: string) => void;
  localPassword: string;
  setLocalPassword: (value: string) => void;
  passwordError: boolean;
  onToggleChange: (configNum: "1" | "5", field: string, value: boolean) => void;
  onInputBlur: (configNum: "1" | "5", field: string, value: string) => void;
}

// Componente WlanBand memoizado para evitar re-renders innecesarios
const WlanBand = React.memo(
  ({
    title,
    band,
    configNum,
    config,
    showPassword,
    setShowPassword,
    localSSID,
    setLocalSSID,
    localPassword,
    setLocalPassword,
    passwordError,
    onToggleChange,
    onInputBlur,
  }: WlanBandProps) => (
    <Card
      sx={{
        width: "100%",
        p: { xs: 2.5, sm: 3 },
        backgroundColor: band === "2.4GHz" ? "hsl(39 89% 97.8%)" : "hsl(224.3 76.3% 97%)",
        border: `2px solid ${band === "2.4GHz" ? "hsl(39 89% 70%)" : "hsl(224.3 76.3% 85%)"}`,
        borderRadius: "14px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            backgroundColor: band === "2.4GHz" ? "hsl(39 89% 85%)" : "hsl(224.3 76.3% 90%)",
            borderRadius: "10px",
          }}
        >
          <Wifi size={24} color={band === "2.4GHz" ? "hsl(39 89% 40%)" : "hsl(224.3 76.3% 40%)"} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "hsl(240 10% 3.9%)" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "hsl(240 3.8% 46.1%)" }}>
            Banda {band}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* SSID */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "hsl(240 3.8% 46.1%)" }}>
              Nombre de Red (SSID)
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "hsl(240 3.8% 60%)" }}>
              {localSSID.length}/12
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            value={localSSID}
            onChange={(e) => {
              const validated = validateSSID(e.target.value);
              setLocalSSID(validated);
            }}
            onBlur={(e) => {
              onInputBlur(configNum, "SSID", e.target.value);
            }}
            placeholder="Ej: MiWifi"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "hsl(0 0% 100%)",
                borderRadius: "8px",
                fontSize: "0.95rem",
                fontWeight: 600,
              },
            }}
          />
          <Typography sx={{ fontSize: "0.75rem", color: "hsl(240 3.8% 60%)", mt: 0.5 }}>
            {band === "5GHz" ? 'Se enviará como: "' + localSSID + '5G"' : "Solo letras, números, espacios, guiones y guiones bajos"}
          </Typography>
        </Box>

        {/* Contraseña */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "hsl(240 3.8% 46.1%)" }}>
              Contraseña
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: passwordError ? "hsl(0 84% 60%)" : "hsl(240 3.8% 60%)" }}>
              {localPassword.length}/10 (Mín. 5)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                size="small"
                type={showPassword ? "text" : "password"}
                value={localPassword}
                onChange={(e) => {
                  const validated = validatePassword(e.target.value);
                  setLocalPassword(validated);
                }}
                onBlur={(e) => onInputBlur(configNum, "PreSharedKey.1.KeyPassphrase", e.target.value)}
                error={passwordError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "hsl(0 0% 100%)",
                    borderRadius: "8px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    borderColor: passwordError ? "hsl(0 84% 60%)" : undefined,
                  },
                }}
              />
              {passwordError && (
                <Alert severity="error" sx={{ mt: 1, fontSize: "0.8rem", py: 0.5 }} icon={<AlertCircle size={14} />}>
                  La contraseña debe tener entre 5 y 10 caracteres
                </Alert>
              )}
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowPassword(!showPassword)}
              sx={{ minWidth: "auto", p: 1, color: "hsl(224.3 76.3% 48%)", mt: 0.5 }}
              title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </Button>
          </Box>
        </Box>

        {/* Estado y Visibilidad */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "hsl(240 3.8% 46.1%)", mb: 0.8 }}>
              Estado del WiFi
            </Typography>
            <ToggleButtonGroup
              value={config.Enable === true ? "active" : "inactive"}
              exclusive
              onChange={(e, newValue) => {
                if (newValue) {
                  onToggleChange(configNum, "Enable", newValue === "active");
                }
              }}
              size="small"
              fullWidth
              sx={{
                "& .MuiToggleButton-root": {
                  flex: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  border: "1px solid hsl(224.3 76.3% 70%)",
                  color: "hsl(240 3.8% 46.1%)",
                  "&.Mui-selected": {
                    backgroundColor: "hsl(142.3 71% 70%)",
                    color: "white",
                    border: "1px solid hsl(142.3 71% 70%)",
                    "&:hover": {
                      backgroundColor: "hsl(142.3 71% 60%)",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="active">Activa</ToggleButton>
              <ToggleButton value="inactive">Desactivada</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "hsl(240 3.8% 46.1%)", mb: 0.8 }}>
              Red WiFi Visible
            </Typography>
            <ToggleButtonGroup
              value={config.SSIDAdvertisementEnabled === true ? "visible" : "hidden"}
              exclusive
              onChange={(e, newValue) => {
                if (newValue) {
                  onToggleChange(configNum, "SSIDAdvertisementEnabled", newValue === "visible");
                }
              }}
              size="small"
              fullWidth
              sx={{
                "& .MuiToggleButton-root": {
                  flex: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  border: "1px solid hsl(224.3 76.3% 70%)",
                  color: "hsl(240 3.8% 46.1%)",
                  "&.Mui-selected": {
                    backgroundColor: "hsl(142.3 71% 70%)",
                    color: "white",
                    border: "1px solid hsl(142.3 71% 70%)",
                    "&:hover": {
                      backgroundColor: "hsl(142.3 71% 60%)",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="visible">Sí</ToggleButton>
              <ToggleButton value="hidden">No</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>
    </Card>
  )
);

WlanBand.displayName = "WlanBand";

export function WifiConfigurationPanel({ model, params, onParamsChange, onChangesUpdate, onValidationErrorsChange, capabilities }: WifiConfigurationPanelProps) {
  const [editedParams, setEditedParams] = React.useState<CwmpWlanParams>(params);
  const [changes, setChanges] = React.useState<{ [key: string]: any }>({});
  const [showPassword2G, setShowPassword2G] = React.useState(false);
  const [showPassword5G, setShowPassword5G] = React.useState(false);
  const [passwordError2G, setPasswordError2G] = React.useState(false);
  const [passwordError5G, setPasswordError5G] = React.useState(false);

  // Estados locales para los inputs (evita re-renderizado en cada carácter)
  const [localSSID2G, setLocalSSID2G] = React.useState<string>("");
  const [localPassword2G, setLocalPassword2G] = React.useState<string>("");
  const [localSSID5G, setLocalSSID5G] = React.useState<string>("");
  const [localPassword5G, setLocalPassword5G] = React.useState<string>("");

  // Determinar si 5G está disponible
  const has5GSupport = capabilities?.["5G"] !== false;

  // Notificar al padre cuando hay errores de validación
  React.useEffect(() => {
    // Si 5GHz no está disponible, no validar sus campos
    const hasValidationErrors = has5GSupport 
      ? (passwordError2G || passwordError5G || localSSID2G.trim().length === 0 || localSSID5G.trim().length === 0)
      : (passwordError2G || localSSID2G.trim().length === 0);
    onValidationErrorsChange?.(hasValidationErrors);
  }, [passwordError2G, passwordError5G, localSSID2G, localSSID5G, onValidationErrorsChange, has5GSupport]);

  // Actualizar parámetros cuando cambian los props
  React.useEffect(() => {
    setEditedParams(params);
    // Inicializar los estados locales con los valores actuales
    const config2G: { [key: string]: any } = {};
    const config5G: { [key: string]: any } = {};

    Object.entries(params).forEach(([key, value]) => {
      const actualValue = typeof value === "object" && value !== null && "_value" in value ? (value as any)._value : value;

      if (key.includes("WLANConfiguration.1.")) {
        const paramKey = key.replace("InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.", "");
        config2G[paramKey] = actualValue;
      } else if (key.includes("WLANConfiguration.5.")) {
        const paramKey = key.replace("InternetGatewayDevice.LANDevice.1.WLANConfiguration.5.", "");
        config5G[paramKey] = actualValue;
      }
    });

    setLocalSSID2G(config2G.SSID || "");
    setLocalPassword2G(config2G["PreSharedKey.1.KeyPassphrase"] || "");
    setLocalSSID5G(config5G.SSID?.replace("5G", "") || ""); // Remover "5G" si existe
    setLocalPassword5G(config5G["PreSharedKey.1.KeyPassphrase"] || "");
  }, [params]);

  // Agrupar parámetros por configuración (1 y 5)
  const extractWlanConfig = React.useCallback(() => {
    const config2G: { [key: string]: any } = {};
    const config5G: { [key: string]: any } = {};

    Object.entries(editedParams).forEach(([key, value]) => {
      const actualValue = typeof value === "object" && value !== null && "_value" in value ? (value as any)._value : value;

      if (key.includes("WLANConfiguration.1.")) {
        const paramKey = key.replace("InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.", "");
        config2G[paramKey] = actualValue;
      } else if (key.includes("WLANConfiguration.5.")) {
        const paramKey = key.replace("InternetGatewayDevice.LANDevice.1.WLANConfiguration.5.", "");
        config5G[paramKey] = actualValue;
      }
    });

    return { config2G, config5G };
  }, [editedParams]);

  const { config2G, config5G } = extractWlanConfig();

  const handleToggleChange = React.useCallback(
    (configNum: "1" | "5", field: string, value: boolean) => {
      const key = `InternetGatewayDevice.LANDevice.1.WLANConfiguration.${configNum}.${field}`;
      const existingParam = editedParams[key];

      // Si existe parámetro anterior, preservar estructura; si no, crear nueva
      const updated = {
        ...editedParams,
        [key]: typeof existingParam === "object" && existingParam !== null ? { ...(existingParam as any), _value: value } : { _value: value, _type: "xsd:boolean", _object: false, _writable: true },
      };
      const updatedChanges = {
        ...changes,
        [key]: typeof existingParam === "object" && existingParam !== null ? { ...(existingParam as any), _value: value } : { _value: value, _type: "xsd:boolean", _object: false, _writable: true },
      };
      setEditedParams(updated);
      setChanges(updatedChanges);
      onParamsChange?.(updated);
      onChangesUpdate?.(updatedChanges);
    },
    [editedParams, changes, onParamsChange, onChangesUpdate]
  );

  const handleInputBlur = React.useCallback(
    (configNum: "1" | "5", field: string, value: string) => {
      // Obtener valores actuales según el config
      const currentSSID = configNum === "1" ? localSSID2G : localSSID5G;
      const currentPassword = configNum === "1" ? localPassword2G : localPassword5G;

      // Validar SSID: no puede estar vacío
      if (field === "SSID") {
        if (!value || value.trim().length === 0) {
          return; // No guardar si SSID está vacío
        }
      }

      // Validar contraseña (mínimo 5, máximo 10 caracteres)
      if (field === "PreSharedKey.1.KeyPassphrase") {
        const isInvalid = value.length < 5 || value.length > 10;
        if (configNum === "1") {
          setPasswordError2G(isInvalid);
        } else {
          setPasswordError5G(isInvalid);
        }
        if (isInvalid) return; // No guardar si la contraseña no cumple los requisitos
      }

      // En este punto, TODAS las validaciones pasaron
      const key = `InternetGatewayDevice.LANDevice.1.WLANConfiguration.${configNum}.${field}`;
      const existingParam = editedParams[key];

      // NO agregar sufijo aquí - se agregará solo al guardar
      const finalValue = value;

      // Si existe parámetro anterior, preservar estructura; si no, crear nueva
      const updated = {
        ...editedParams,
        [key]: typeof existingParam === "object" && existingParam !== null ? { ...(existingParam as any), _value: finalValue } : { _value: finalValue, _type: field === "PreSharedKey.1.KeyPassphrase" ? "xsd:string" : "xsd:string", _object: false, _writable: true },
      };
      const updatedChanges = {
        ...changes,
        [key]: typeof existingParam === "object" && existingParam !== null ? { ...(existingParam as any), _value: finalValue } : { _value: finalValue, _type: field === "PreSharedKey.1.KeyPassphrase" ? "xsd:string" : "xsd:string", _object: false, _writable: true },
      };
      setEditedParams(updated);
      setChanges(updatedChanges);
      onParamsChange?.(updated);
      onChangesUpdate?.(updatedChanges);
    },
    [editedParams, changes, localSSID2G, localPassword2G, localSSID5G, localPassword5G, onParamsChange, onChangesUpdate]
  );

  return (
    <>
      <WlanBand
        title="WiFi 2.4GHz"
        band="2.4GHz"
        configNum="1"
        config={config2G}
        showPassword={showPassword2G}
        setShowPassword={setShowPassword2G}
        localSSID={localSSID2G}
        setLocalSSID={setLocalSSID2G}
        localPassword={localPassword2G}
        setLocalPassword={setLocalPassword2G}
        passwordError={passwordError2G}
        onToggleChange={handleToggleChange}
        onInputBlur={handleInputBlur}
      />
      
      {has5GSupport ? (
        <WlanBand
          title="WiFi 5GHz"
          band="5GHz"
          configNum="5"
          config={config5G}
          showPassword={showPassword5G}
          setShowPassword={setShowPassword5G}
          localSSID={localSSID5G}
          setLocalSSID={setLocalSSID5G}
          localPassword={localPassword5G}
          setLocalPassword={setLocalPassword5G}
          passwordError={passwordError5G}
          onToggleChange={handleToggleChange}
          onInputBlur={handleInputBlur}
        />
      ) : null}
    </>
  );
}
